import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from './ui/button';
import { ScrollArea } from './ui/scroll-area';
import AIChatAssistant from './AIChatAssistant';
import api from '../lib/api';
import { Badge } from './ui/badge';
import {
    LayoutDashboard,
    Server,
    Ticket,
    FileText,
    Users,
    Building2,
    Bell,
    LogOut,
    Menu,
    FileSignature,
    ChevronRight,
    ChevronDown,
    ShieldCheck,
    Briefcase,
    Hotel,
    Monitor,
    Wine,
    MessageSquare,
    Settings,
    Utensils,
    Star,
    ClipboardList,
    Package,
    UserCheck,
    Wrench,
    Sparkles,
    Laptop,
    Coffee,
    Waves,
    Umbrella,
    Music,
    Building,
    Wifi,
    Box,
    Warehouse,
    ClipboardCheck,
    Share2,
    UtensilsCrossed,
    BarChart3
} from 'lucide-react';

const LOGO_URL = "/kaptan-hotels-logo.png";

// Kategorili menü yapısı
const adminMenuStructure = [
    {
        id: 'dashboard',
        label: 'Dashboard',
        icon: LayoutDashboard,
        href: '/dashboard',
    },
    {
        id: 'operations',
        label: 'Operasyon Merkezi',
        icon: ClipboardCheck,
        showBadge: true,
        children: [
            { href: '/operations/order-taker', label: 'Order-Taker', icon: ClipboardList },
            { href: '/operations/on-buro', label: 'Ön Büro', icon: UserCheck, depBadge: 'on_buro' },
            { href: '/operations/teknik', label: 'Teknik', icon: Wrench, depBadge: 'teknik' },
            { href: '/operations/kat-hizmetleri', label: 'Kat Hizmetleri', icon: Sparkles, depBadge: 'kat_hizmetleri' },
            { href: '/operations/servis', label: 'Servis', icon: Utensils, depBadge: 'servis' },
            { href: '/operations/sikayet', label: 'Şikayet', icon: MessageSquare, depBadge: 'sikayet' },
            { href: '/operations/raporlar', label: 'Raporlar', icon: FileText },
        ]
    },
    {
        id: 'bar-stock',
        label: 'Bar Stok',
        icon: Wine,
        children: [
            { href: '/products', label: 'Ürünler', icon: Box },
            { href: '/bar/lobby', label: 'Lobby Bar', icon: Coffee },
            { href: '/bar/servis', label: 'Servis Bar', icon: Utensils },
            { href: '/bar/vitamin', label: 'Vitamin Bar', icon: Wine },
            { href: '/bar/havuz', label: 'Havuz Bar', icon: Waves },
            { href: '/bar/plaj', label: 'Plaj Bar', icon: Umbrella },
            { href: '/bar/amfi', label: 'Amfi Bar', icon: Music },
            { href: '/bar/teras', label: 'Teras Bar', icon: Building },
            { href: '/bar/alacarte', label: 'Alacarte Bar', icon: Wine },
            { href: '/minibar', label: 'Minibar', icon: Coffee },
        ]
    },
    // NOTE: "Snack Bar" sekmesi geçici olarak gizlendi (kullanıcı talebi).
    // Yeniden etkinleştirmek için aşağıdaki bloğu geri yorumdan çıkarın:
    // { id: 'snack-bar', label: 'Snack Bar', icon: UtensilsCrossed, href: '/snack-bar' },
    {
        id: 'stock-ops',
        label: 'Stok İşlemleri',
        icon: Package,
        children: [
            { href: '/stock/talepler', label: 'Talepler', icon: ClipboardList },
            { href: '/stock/depo', label: 'Depo', icon: Warehouse },
            { href: '/stock/sayim', label: 'Sayım', icon: ClipboardCheck },
        ]
    },
    {
        id: 'management',
        label: 'Yönetim',
        icon: Settings,
        children: [
            { href: '/management/users', label: 'Kullanıcılar', icon: Users },
            { href: '/management/reports', label: 'Raporlar', icon: FileText },
            { href: '/management/social', label: 'Sosyal Medya', icon: Share2 },
            { href: '/management/backup', label: 'Yedekleme', icon: Server },
            { href: '/management/settings', label: 'Ayarlar', icon: Settings },
        ]
    },
];

// Yönetim rolü için menü - sadece Dashboard + Bar Stok + Raporlar
const managerMenuStructure = [
    {
        id: 'dashboard',
        label: 'Dashboard',
        icon: LayoutDashboard,
        href: '/dashboard',
    },
    {
        id: 'bar-stock',
        label: 'Bar Stok',
        icon: Wine,
        children: [
            { href: '/bar/lobby', label: 'Lobby Bar', icon: Coffee },
            { href: '/bar/servis', label: 'Servis Bar', icon: Utensils },
            { href: '/bar/vitamin', label: 'Vitamin Bar', icon: Wine },
            { href: '/bar/havuz', label: 'Havuz Bar', icon: Waves },
            { href: '/bar/plaj', label: 'Plaj Bar', icon: Umbrella },
            { href: '/bar/amfi', label: 'Amfi Bar', icon: Music },
            { href: '/bar/teras', label: 'Teras Bar', icon: Building },
            { href: '/bar/alacarte', label: 'Alacarte Bar', icon: Wine },
            { href: '/minibar', label: 'Minibar', icon: Coffee },
        ]
    },
    // NOTE: "Snack Bar" sekmesi geçici olarak gizlendi (kullanıcı talebi).
    // { id: 'snack-bar', label: 'Snack Bar', icon: UtensilsCrossed, href: '/snack-bar' },
    {
        id: 'reports',
        label: 'Raporlar',
        icon: FileText,
        href: '/management/reports',
    },
    {
        id: 'social',
        label: 'Sosyal Medya',
        icon: Share2,
        href: '/management/social',
    },
];

const customerNavItems = [
    { href: '/dashboard', label: 'Dashboard', icon: LayoutDashboard },
    { href: '/assets', label: 'Varlıklarım', icon: Server },
    { href: '/cameras', label: 'Kamera', icon: FileText },
    { href: '/access-points', label: 'Access Point', icon: Wifi },
];

const technicianNavItems = [
    { href: '/dashboard', label: 'Dashboard', icon: LayoutDashboard },
    { href: '/assets', label: 'Varlık Yönetimi', icon: Server },
    { href: '/cameras', label: 'Kamera', icon: FileText },
];

export function Layout({ children }) {
    const { user, logout, isAdmin, isManager, isStaff, isTechnician, isGarson, isUstYonetici, isYonetici, isPersonel, isBarSef, userDepartment, allowedSections } = useAuth();
    const location = useLocation();
    const navigate = useNavigate();
    const [sidebarOpen, setSidebarOpen] = useState(false);
    const [expandedMenus, setExpandedMenus] = useState([]); // Varsayılan tüm menüler kapalı
    const [opsBadge, setOpsBadge] = useState(0);
    const [depBadges, setDepBadges] = useState({}); // {teknik:3, on_buro:2, ..., sikayet:1}

    // Operasyon Merkezi badge — açık talep sayısını çek
    useEffect(() => {
        const fetchBadge = async () => {
            try {
                const [r1, r2] = await Promise.all([
                    api.get('/operations/requests/badge'),
                    api.get('/operations/badges/by-department'),
                ]);
                setOpsBadge(r1.data?.count || 0);
                const by = r2.data?.by_department || {};
                setDepBadges({ ...by, sikayet: r2.data?.sikayet || 0 });
            } catch { setOpsBadge(0); setDepBadges({}); }
        };
        fetchBadge();
        const id = setInterval(fetchBadge, 30000);
        return () => clearInterval(id);
    }, [location.pathname]);

    const toggleMenu = (menuId) => {
        setExpandedMenus(prev => 
            prev.includes(menuId) 
                ? prev.filter(id => id !== menuId)
                : [...prev, menuId]
        );
    };

    // Operations grup için role/section filtreleme
    const filterOperationsMenu = (menu) => {
        // ust_yonetici → her şeyi görür (backend writes blocks)
        if (isUstYonetici) return menu;
        // yonetici → SADECE allowed_sections içindeki departman alt-linkleri + Şikayet
        // Order-Taker ve Raporlar (tüm-departman) yonetici'ye KAPALI.
        if (isYonetici) {
            const childMap = {
                on_buro: '/operations/on-buro', teknik: '/operations/teknik',
                kat_hizmetleri: '/operations/kat-hizmetleri', servis: '/operations/servis',
            };
            const allowedDepHrefs = new Set((allowedSections || []).map(s => childMap[s]).filter(Boolean));
            const alwaysShown = new Set(['/operations/sikayet']); // tüm-departman değil; ama departman bazlı Şikayet erişimi için
            return menu.map(item => {
                if (item.id === 'operations') {
                    const children = (item.children || []).filter(c => alwaysShown.has(c.href) || allowedDepHrefs.has(c.href));
                    return { ...item, children };
                }
                return item;
            }).filter(it => it.id === 'operations' || it.id === 'dashboard');
        }
        // personel → sadece kendi departmanı + dashboard
        if (isPersonel) {
            const slugMap = { teknik: '/operations/teknik', on_buro: '/operations/on-buro', kat_hizmetleri: '/operations/kat-hizmetleri', servis: '/operations/servis' };
            const slug = slugMap[userDepartment];
            return menu.map(item => {
                if (item.id === 'operations') {
                    const children = (item.children || []).filter(c => slug && c.href === slug);
                    return { ...item, children };
                }
                return item;
            }).filter(it => it.id === 'operations' || it.id === 'dashboard');
        }
        return menu;
    };

    // Get the correct menu based on role
    const getMenuStructure = () => {
        if (isAdmin) return adminMenuStructure;
        if (isUstYonetici) return filterOperationsMenu(adminMenuStructure);
        if (isYonetici || isPersonel) return filterOperationsMenu(adminMenuStructure);
        if (isManager) return managerMenuStructure;
        if (isBarSef) {
            // Bar Şef: Dashboard + tüm Bar Stok + Stok İşlemleri (Talepler/Depo/Sayım) + Ürünler (read-only)
            return [
                { id: 'dashboard', label: 'Dashboard', icon: LayoutDashboard, href: '/dashboard' },
                {
                    id: 'bar-stock',
                    label: 'Bar Stok',
                    icon: Wine,
                    children: [
                        { href: '/products', label: 'Ürünler', icon: Box },
                        { href: '/bar/lobby', label: 'Lobby Bar', icon: Coffee },
                        { href: '/bar/servis', label: 'Servis Bar', icon: Utensils },
                        { href: '/bar/vitamin', label: 'Vitamin Bar', icon: Wine },
                        { href: '/bar/havuz', label: 'Havuz Bar', icon: Waves },
                        { href: '/bar/plaj', label: 'Plaj Bar', icon: Umbrella },
                        { href: '/bar/amfi', label: 'Amfi Bar', icon: Music },
                        { href: '/bar/teras', label: 'Teras Bar', icon: Building },
                        { href: '/bar/alacarte', label: 'Alacarte Bar', icon: Wine },
                        { href: '/minibar', label: 'Minibar', icon: Coffee },
                    ],
                },
                {
                    id: 'stock-ops',
                    label: 'Stok İşlemleri',
                    icon: Package,
                    children: [
                        { href: '/stock/talepler', label: 'Talepler', icon: ClipboardList },
                        { href: '/stock/depo', label: 'Depo', icon: Warehouse },
                        { href: '/stock/sayim', label: 'Sayım', icon: ClipboardCheck },
                    ],
                },
            ];
        }
        if (isGarson) {
            // Garson: minimal menü - Snack Bar POS (kendi sipariş girer + KDS/Sıramatik takip eder)
            return [
                { id: 'dashboard', label: 'Dashboard', icon: LayoutDashboard, href: '/dashboard' },
                { id: 'snack-bar', label: 'Snack Bar POS', icon: UtensilsCrossed, href: '/snack-bar' },
            ];
        }
        if (isStaff) {
            // Personel: tum barlari gorur, sadece "Bugun Sayim" alanini degistirebilir
            const allBars = [
                { href: '/bar/lobby', label: 'Lobby Bar', icon: Coffee },
                { href: '/bar/servis', label: 'Servis Bar', icon: Utensils },
                { href: '/bar/vitamin', label: 'Vitamin Bar', icon: Wine },
                { href: '/bar/havuz', label: 'Havuz Bar', icon: Waves },
                { href: '/bar/plaj', label: 'Plaj Bar', icon: Umbrella },
                { href: '/bar/amfi', label: 'Amfi Bar', icon: Music },
                { href: '/bar/teras', label: 'Teras Bar', icon: Building },
                { href: '/bar/alacarte', label: 'Alacarte Bar', icon: Wine },
            { href: '/minibar', label: 'Minibar', icon: Coffee },
            ];
            return [
                { id: 'dashboard', label: 'Dashboard', icon: LayoutDashboard, href: '/dashboard' },
                {
                    id: 'bar-stock',
                    label: 'Bar Stok',
                    icon: Wine,
                    children: allBars,
                },
            ];
        }
        return [];
    };

    const currentMenu = getMenuStructure();

    const handleLogout = () => {
        logout();
        navigate('/login');
    };

    const isPathActive = (href) => {
        return location.pathname === href || location.pathname.startsWith(href + '/');
    };

    const isMenuActive = (menu) => {
        if (menu.href) return isPathActive(menu.href);
        if (menu.children) {
            return menu.children.some(child => {
                if (child.href) return isPathActive(child.href);
                if (child.subChildren) return child.subChildren.some(sub => isPathActive(sub.href));
                return false;
            });
        }
        return false;
    };

    // Admin/Manager/Staff için kategorili menü
    const renderAdminMenu = () => (
        <nav className="px-3 space-y-1">
            {currentMenu.map((item) => (
                <div key={item.id}>
                    {item.href ? (
                        // Tek sayfa linki (Dashboard gibi)
                        <Link
                            to={item.href}
                            onClick={() => setSidebarOpen(false)}
                            data-testid={`nav-${item.id}`}
                            className={`flex items-center gap-3 px-4 py-3 rounded-lg transition-all duration-200 ${
                                isPathActive(item.href)
                                    ? 'bg-white/15 text-white'
                                    : 'text-white/70 hover:bg-white/10 hover:text-white'
                            }`}
                        >
                            <item.icon className="w-5 h-5 flex-shrink-0" />
                            <span className="font-medium">{item.label}</span>
                            {item.showBadge && opsBadge > 0 && (
                                <span className="ml-auto bg-red-500 text-white text-[10px] font-bold rounded-full min-w-5 h-5 px-1.5 flex items-center justify-center" data-testid="ops-badge">
                                    {opsBadge}
                                </span>
                            )}
                        </Link>
                    ) : (
                        // Açılır menü
                        <>
                            <button
                                onClick={() => toggleMenu(item.id)}
                                data-testid={`nav-${item.id}`}
                                className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg transition-all duration-200 ${
                                    isMenuActive(item)
                                        ? 'bg-white/15 text-white'
                                        : 'text-white/70 hover:bg-white/10 hover:text-white'
                                }`}
                            >
                                <item.icon className="w-5 h-5 flex-shrink-0" />
                                <span className="font-medium flex-1 text-left">{item.label}</span>
                                {item.showBadge && opsBadge > 0 && (
                                    <span className="bg-red-500 text-white text-[10px] font-bold rounded-full min-w-5 h-5 px-1.5 flex items-center justify-center" data-testid="ops-badge">
                                        {opsBadge}
                                    </span>
                                )}
                                {expandedMenus.includes(item.id) ? (
                                    <ChevronDown className="w-4 h-4" />
                                ) : (
                                    <ChevronRight className="w-4 h-4" />
                                )}
                            </button>
                            
                            {/* Alt menü */}
                            {expandedMenus.includes(item.id) && item.children && (
                                <div className="ml-4 mt-1 space-y-1 border-l border-white/20 pl-2">
                                    {item.children.map((child, idx) => (
                                        <div key={idx}>
                                            {child.href ? (
                                                <Link
                                                    to={child.href}
                                                    onClick={() => setSidebarOpen(false)}
                                                    data-testid={`nav-${child.href.replace(/\//g, '-')}`}
                                                    className={`flex items-center gap-2 px-3 py-2 rounded-md text-sm transition-all ${
                                                        isPathActive(child.href)
                                                            ? 'bg-white/10 text-white'
                                                            : 'text-white/60 hover:bg-white/5 hover:text-white'
                                                    }`}
                                                >
                                                    <child.icon className="w-4 h-4" />
                                                    <span className="flex-1">{child.label}</span>
                                                    {child.depBadge && depBadges[child.depBadge] > 0 && (
                                                        <span
                                                            className="bg-red-500 text-white text-[10px] font-bold rounded-full min-w-5 h-5 px-1.5 flex items-center justify-center"
                                                            data-testid={`dep-badge-${child.depBadge}`}
                                                        >
                                                            {depBadges[child.depBadge]}
                                                        </span>
                                                    )}
                                                </Link>
                                            ) : child.subChildren ? (
                                                // Departmanlar gibi iç içe menü
                                                <div>
                                                    <div className="flex items-center gap-2 px-3 py-2 text-sm text-white/50">
                                                        <child.icon className="w-4 h-4" />
                                                        <span>{child.label}</span>
                                                    </div>
                                                    <div className="ml-4 space-y-1">
                                                        {child.subChildren.map((sub, subIdx) => (
                                                            <Link
                                                                key={subIdx}
                                                                to={sub.href}
                                                                onClick={() => setSidebarOpen(false)}
                                                                data-testid={`nav-${sub.href.replace(/\//g, '-')}`}
                                                                className={`flex items-center gap-2 px-3 py-1.5 rounded-md text-xs transition-all ${
                                                                    isPathActive(sub.href)
                                                                        ? 'bg-white/10 text-white'
                                                                        : 'text-white/50 hover:bg-white/5 hover:text-white'
                                                                }`}
                                                            >
                                                                <sub.icon className="w-3 h-3" />
                                                                <span>{sub.label}</span>
                                                            </Link>
                                                        ))}
                                                    </div>
                                                </div>
                                            ) : null}
                                        </div>
                                    ))}
                                </div>
                            )}
                        </>
                    )}
                </div>
            ))}
        </nav>
    );

    // Müşteri/Teknisyen için düz menü
    const renderSimpleMenu = (items) => (
        <nav className="px-3 space-y-1">
            {items.map((item) => (
                <Link
                    key={item.href}
                    to={item.href}
                    onClick={() => setSidebarOpen(false)}
                    data-testid={`nav-${item.href.replace('/', '')}`}
                    className={`flex items-center gap-3 px-4 py-3 rounded-lg transition-all duration-200 ${
                        isPathActive(item.href)
                            ? 'bg-white/15 text-white'
                            : 'text-white/70 hover:bg-white/10 hover:text-white'
                    }`}
                >
                    <item.icon className="w-5 h-5 flex-shrink-0" />
                    <span className="font-medium">{item.label}</span>
                    {isPathActive(item.href) && <ChevronRight className="w-4 h-4 ml-auto" />}
                </Link>
            ))}
        </nav>
    );

    // Sayfa başlığını bul
    const getPageTitle = () => {
        const path = location.pathname;
        
        // Admin menüsünde ara
        for (const menu of adminMenuStructure) {
            if (menu.href === path) return menu.label;
            if (menu.children) {
                for (const child of menu.children) {
                    if (child.href === path) return child.label;
                    if (child.subChildren) {
                        for (const sub of child.subChildren) {
                            if (sub.href === path) return sub.label;
                        }
                    }
                }
            }
        }
        
        // Müşteri/teknisyen menüsünde ara
        const simpleItems = isTechnician ? technicianNavItems : customerNavItems;
        const found = simpleItems.find(i => path.startsWith(i.href));
        if (found) return found.label;
        
        return 'Portal';
    };

    return (
        <div className="min-h-screen bg-slate-50 flex">
            {/* Mobile/Tablet Overlay */}
            {sidebarOpen && (
                <div
                    className="fixed inset-0 bg-black/50 z-40 lg:hidden"
                    onClick={() => setSidebarOpen(false)}
                />
            )}

            {/* Sidebar */}
            <aside
                className={`fixed lg:sticky top-0 left-0 z-50 w-72 h-screen bg-gradient-to-b from-slate-800 to-slate-900 flex flex-col transform transition-transform duration-300 ${
                    sidebarOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'
                }`}
            >
                {/* Logo */}
                <div className="p-6 border-b border-white/10 flex justify-center">
                    <Link to="/dashboard" className="block" data-testid="logo-link">
                        <img
                            src="/kaptan-hotels-logo-light.png"
                            alt="Kaptan Hotels"
                            className="h-24 w-auto object-contain"
                        />
                    </Link>
                </div>

                {/* Navigation */}
                <ScrollArea className="flex-1 py-4">
                    {(isAdmin || isManager || isStaff || isGarson || isUstYonetici || isYonetici || isPersonel || isBarSef) ? renderAdminMenu() : renderSimpleMenu(isTechnician ? technicianNavItems : customerNavItems)}
                </ScrollArea>

                {/* User Info */}
                <div className="p-4 border-t border-white/10">
                    <div className="flex items-center gap-3 px-3 py-2 mb-2">
                        <div className="w-10 h-10 rounded-full bg-orwion-green flex items-center justify-center text-white font-semibold">
                            {user?.name?.charAt(0)?.toUpperCase()}
                        </div>
                        <div className="flex-1 min-w-0">
                            <p className="text-white font-medium text-sm truncate">{user?.name}</p>
                            <p className="text-white/60 text-xs truncate">{user?.email}</p>
                        </div>
                    </div>
                    <Button
                        variant="ghost"
                        onClick={handleLogout}
                        data-testid="logout-btn"
                        className="w-full justify-start text-white/70 hover:text-white hover:bg-white/10"
                    >
                        <LogOut className="w-4 h-4 mr-2" />
                        Çıkış Yap
                    </Button>
                </div>
            </aside>

            {/* Main Content */}
            <div className="flex-1 flex flex-col min-w-0">
                {/* Header */}
                <header className="h-16 bg-white border-b border-slate-200 flex items-center px-4 md:px-6 justify-between sticky top-0 z-30 shadow-sm">
                    <div className="flex items-center gap-4">
                        <Button
                            variant="ghost"
                            size="icon"
                            className="lg:hidden"
                            onClick={() => setSidebarOpen(true)}
                            data-testid="mobile-menu-btn"
                        >
                            <Menu className="w-5 h-5" />
                        </Button>
                        <div className="hidden lg:block">
                            <h1 className="font-heading font-semibold text-lg text-slate-800">
                                {getPageTitle()}
                            </h1>
                        </div>
                    </div>

                    <div className="flex items-center gap-3">
                        <Badge variant="outline" className="hidden sm:flex gap-1 items-center">
                            {isAdmin ? <ShieldCheck className="w-3 h-3" /> : <Briefcase className="w-3 h-3" />}
                            {isAdmin ? 'Admin' : isUstYonetici ? 'Üst Yönetici' : isYonetici ? 'Yönetici' : isPersonel ? 'Personel' : isBarSef ? 'Bar Şef' : isManager ? 'Yönetim' : isGarson ? 'Garson' : isStaff ? 'Personel' : isTechnician ? 'Teknisyen' : 'Müşteri'}
                        </Badge>
                        <Link to="/notifications" data-testid="notifications-btn">
                            <Button variant="ghost" size="icon" className="relative">
                                <Bell className="w-5 h-5" />
                            </Button>
                        </Link>
                    </div>
                </header>

                {/* Page Content */}
                <main className="flex-1 overflow-y-auto p-2 sm:p-3 md:p-4 lg:p-8">
                    <div className="animate-fade-in">
                        {children}
                    </div>
                </main>
            </div>

            {/* AI Chat Asistanı — tüm sayfalarda erişilebilir */}
            <AIChatAssistant />
        </div>
    );
}

export default Layout;
